// Delete comment confirmation dialog

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useDeleteComment } from '@/hooks/useComments'
import { Loader2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

interface DeleteCommentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  permlink: string
  rootAuthor: string
  rootPermlink: string
  onSuccess?: () => void
}

export function DeleteCommentDialog({
  open,
  onOpenChange,
  permlink,
  rootAuthor,
  rootPermlink,
  onSuccess,
}: DeleteCommentDialogProps) {
  const deleteComment = useDeleteComment()

  const handleDelete = async () => {
    try {
      await deleteComment.mutateAsync({
        permlink,
        rootAuthor,
        rootPermlink,
      })
      toast.success('댓글이 삭제되었습니다')
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      toast.error('댓글 삭제 실패', {
        description: error instanceof Error ? error.message : '알 수 없는 오류',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>댓글 삭제</DialogTitle>
          <DialogDescription>
            이 댓글을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
          <p className="font-medium text-foreground mb-1">삭제 조건:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>작성 후 7일 이내만 삭제 가능</li>
            <li>답글이 없는 댓글만 삭제 가능</li>
          </ul>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={deleteComment.isPending}
          >
            취소
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteComment.isPending}
          >
            {deleteComment.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                삭제 중...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                삭제
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
